import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn } from '@angular/router';
import { Store } from '@ngrx/store';
import { catchError, first, map } from 'rxjs/operators';
import { Observable, of } from 'rxjs';

import { AppState } from '../../store';
import { Tenant } from '../../models/tenant.model';
import { TenantsDBService } from '../../services/tenants-db.service';
import { TenantService } from '../../services/tenant.service';
import { setTenantInfo } from '../../store/platform.actions';
import { setSessionLanguage, themeChanged } from '../../store/platform.actions';
import { checkIfSingleSignOnPage, DEFAULT_THEME } from '../../common/platform-utils';
import { environment } from '../../../environments/environment';

/** The school's subdomain, when the page is being served from one. Mirrors PlatformGuard. */
function platformSubdomain(): string | undefined {
  const matches = document.location.host.match(new RegExp(`^(.*).${environment.domainName}`));
  return matches && matches.length === 2 ? matches[1] : undefined;
}

/**
 * Loads the school behind this page, then lets it render.
 *
 * Two jobs, and the second one is not optional: app.component only renders its router outlet once
 * a brand theme is in the store, so a route that never puts one there paints nothing at all. Every
 * other student-facing route gets that from PlatformGuard, which this page deliberately does not
 * use - PlatformGuard can redirect, and this page has to survive being opened by a logged-out
 * student holding a dead link. So it resolves the school the same way PlatformGuard does, from the
 * hostname, and dispatches the theme itself.
 *
 * It always returns true. A school we cannot look up should cost the student the school's colours,
 * never the page - this is the last thing standing between them and a dead end.
 */
export const authActionBrandGuard: CanActivateFn = (route: ActivatedRouteSnapshot) => {
  const store = inject<Store<AppState>>(Store);
  const tenantsDB = inject(TenantsDBService);
  const tenantService = inject(TenantService);

  const applyTenant = (tenant: Tenant | null | undefined): boolean => {
    if (!tenant) {
      // Still needs a theme, or the shell renders nothing and the student sees a blank page.
      store.dispatch(themeChanged(DEFAULT_THEME));
      store.dispatch(setSessionLanguage({ language: 'en' }));
      return true;
    }

    tenantService.id = tenant.id;
    tenantService.tenant = tenant;
    store.dispatch(setTenantInfo({ tenantInfo: { ...tenant } as any }));
    store.dispatch(themeChanged(tenant.brandTheme ?? DEFAULT_THEME));
    store.dispatch(
      setSessionLanguage({ language: tenant?.languageConfig?.siteLanguage ?? 'en' })
    );
    return true;
  };

  const settle = (source$: Observable<Tenant>) =>
    source$.pipe(
      first(),
      map(applyTenant),
      catchError(() => of(applyTenant(null)))
    );

  // login.onlinecoursehost.com carries the school in the query string; its own hostname says nothing.
  if (checkIfSingleSignOnPage()) {
    const tenantId = route.queryParamMap.get('tenantId');
    return tenantId ? settle(tenantsDB.findTenantById(tenantId)) : applyTenant(null);
  }

  const subDomain = platformSubdomain();
  return subDomain
    ? settle(tenantsDB.findTenantBySubdomain(subDomain))
    : settle(tenantsDB.findTenantByCustomDomain(window.location.hostname));
};
